/**
 * Sensor Service - Handles all sensor data-related operations in Firestore
 */

import { db, firebase } from '../firebase';
import { handleFirestoreError } from '../utils';
import {
	createCacheKey,
	fetchWithCache,
	invalidateCache,
	DEFAULT_CACHE_TTL_MS,
} from '../../../utils/firestoreCache';

const NODE_CACHE_NAMESPACE = "dbNodes";
const NODE_CACHE_MS = DEFAULT_CACHE_TTL_MS; // 60 seconds
const MAX_BATCH_WRITE_OPERATIONS = 450;

function normalizeGroupName(group) {
	return typeof group === "string" ? group.trim() : "";
}

async function updateRefsInChunks(refs, payload) {
	if (!Array.isArray(refs) || refs.length === 0) {
		return 0;
	}

	let updatedCount = 0;
	for (let i = 0; i < refs.length; i += MAX_BATCH_WRITE_OPERATIONS) {
		const chunk = refs.slice(i, i + MAX_BATCH_WRITE_OPERATIONS);
		const batch = db.batch();
		chunk.forEach((ref) => {
			batch.update(ref, payload);
		});
		await batch.commit();
		updatedCount += chunk.length;
	}

	return updatedCount;
}

/**
 * Gets all nodes for a user
 * @param {string} userID - The unique identifier for the user
 * @param {Object} [options] - Cache options
 * @param {boolean} [options.forceRefresh=false] - Force refresh from Firestore
 * @returns {Promise<Array>} Array of node objects
 */
export async function getAllNodes(userID, options = {}) {
	const { forceRefresh = false } = options;
	const cacheKey = createCacheKey(NODE_CACHE_NAMESPACE, userID);

	return fetchWithCache(
		cacheKey,
		async () => {
			try {
				const nodes = [];
				const snapshot = await db
					.collection("Users")
					.doc(userID)
					.collection("Nodes")
					.get();

				snapshot.forEach((nodeSnapshot) => {
					const node = nodeSnapshot.data();
					node.ID = node.Id ? node.Id : node.ID;
					node.documentId = nodeSnapshot.id;
					nodes.push(node);
				});

				return nodes;
			} catch (error) {
				console.error("[sensorService][getAllNodes] - Error getting nodes:", error);
				handleFirestoreError(error);
				throw error;
			}
		},
		{ ttlMs: NODE_CACHE_MS, forceRefresh }
	);
}

/**
 * Invalidates the node cache for a user
 * @param {string} userID - The unique identifier for the user
 */
export function invalidateNodeCache(userID) {
	if (!userID) return;
	invalidateCache(`${NODE_CACHE_NAMESPACE}:["${userID}"`);
}

/**
 * Deletes a BioNode for a user
 * Removes documents from User subcollections and updates global BioNode
 * @param {string} userID - The unique identifier for the user
 * @param {string} nodeID - The ID of the node to delete (BioNode ID)
 * @param {string|null} userNodeID - Optional document ID for the user-specific node document. If null, nodeID is used.
 * @returns {Promise<boolean>} Success status
 */
export async function deleteBioNode(userID, nodeID, userNodeID = null) {
	try {
		const batch = db.batch();

		// 1. Delete document at /Users/{userId}/BioNodes/{nodeId}
		// Assuming BioNodes subcollection uses the BioNode ID as key
		const userBioNodeRef = db
			.collection("Users")
			.doc(userID)
			.collection("BioNodes")
			.doc(nodeID);
		batch.delete(userBioNodeRef);

		// 2. Delete document at /Users/{userId}/Nodes/{userNodeID || nodeID}
		// Uses the specific document ID if provided, otherwise assumes it matches nodeID
		const targetUserNodeID = userNodeID || nodeID;
		const userNodeRef = db
			.collection("Users")
			.doc(userID)
			.collection("Nodes")
			.doc(targetUserNodeID);
		batch.delete(userNodeRef);

		// 3. Update document at /BioNodes/{nodeId}
		const globalBioNodeRef = db.collection("BioNodes").doc(nodeID);
		const globalBioNodeDoc = await globalBioNodeRef.get();

		if (globalBioNodeDoc.exists) {
			batch.update(globalBioNodeRef, {
				User: firebase.firestore.FieldValue.delete(),
				Active: false,
			});
		} else {
			console.warn(
				`[sensorService][deleteBioNode] - Global BioNode ${nodeID} does not exist. Skipping update.`
			);
		}

		await batch.commit();
		return true;
	} catch (error) {
		console.error(
			"[sensorService][deleteBioNode] - Error deleting BioNode:",
			error
		);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Retrieves metadata for specified nodes
 * @param {string} userID - The unique identifier for the user
 * @param {Array<{Group: string}>} nodes - Array of node objects to fetch metadata for
 * @returns {Promise<Array>} Array of metadata objects with timestamp and sensor readings
 */
export async function getMetaDataOfNodes(userID, nodes) {
	if (!nodes || nodes.length === 0) return [];

	let metaData = [];

	try {
		const promises = nodes.map(async (node) => {
			// Replace '/' with '__' to match backend Firestore document ID sanitization
			const safeGroup = node.Group.replace(/\//g, "__");
			const snapshot = await db
				.collection("Users")
				.doc(userID)
				.collection("Meta")
				.doc(safeGroup)
				.collection("Sensordata")
				.doc("Day")
				.get();

			if (snapshot.exists && snapshot.data()?.Sensordata) {
				const sensorData = snapshot.data().Sensordata;
				return sensorData.map((data) => ({
					t: data.t,
					cMean: data.cMean || data.c,
					aMean: data.aMean || data.a,
				}));
			}

			return [];
		});

		const results = await Promise.all(promises);
		metaData = results.flat();
		metaData.sort((a, b) => a.t - b.t);
	} catch (error) {
		console.error("[sensorService][getMetaDataOfNodes] - Error fetching metadata:", error);
		handleFirestoreError(error);
		throw error;
	}

	return metaData;
}

/**
 * Changes the group of a node
 * @param {string} userID - The unique identifier for the user
 * @param {string} node - Node ID
 * @param {string} group - New group to assign
 * @returns {Promise<boolean>} Success status
 */
export async function changeGroupOfNodes(userID, node, group) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Nodes")
			.doc(node)
			.set(
				{
					Group: group,
				},
				{ merge: true }
			)
			.catch((e) => {
				throw e;
			});
		return true;
	} catch (error) {
		console.error("[sensorService][changeGroupOfNodes] - Error changing node group:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Renames a BioNode group for a user.
 * Updates user node docs (`Users/{userID}/Nodes`) and matching global BioNodes (`BioNodes`).
 * Intentionally does not migrate historical Meta docs or rename animal Group fields.
 * @param {string} userID - The unique identifier for the user
 * @param {string} oldGroup - Existing group name
 * @param {string} newGroup - New group name
 * @returns {Promise<{oldGroup:string,newGroup:string,matchedUserNodes:number,updatedUserNodes:number,matchedGlobalNodes:number,updatedGlobalNodes:number}>}
 */
export async function renameNodeGroupForUser(userID, oldGroup, newGroup) {
	try {
		if (!userID || typeof userID !== "string") {
			throw new Error("User ID is required");
		}

		const normalizedOldGroup = normalizeGroupName(oldGroup);
		const normalizedNewGroup = normalizeGroupName(newGroup);

		if (!normalizedOldGroup || !normalizedNewGroup) {
			throw new Error("Group names must be non-empty");
		}

		if (normalizedOldGroup === normalizedNewGroup) {
			throw new Error("New group name must be different from old group name");
		}

		const userNodesSnapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("Nodes")
			.where("Group", "==", normalizedOldGroup)
			.get();

		const globalNodesSnapshot = await db
			.collection("BioNodes")
			.where("User", "==", userID)
			.where("Group", "==", normalizedOldGroup)
			.get();

		const userNodeRefs = [];
		userNodesSnapshot.forEach((docSnapshot) => {
			userNodeRefs.push(docSnapshot.ref);
		});

		const globalNodeRefs = [];
		globalNodesSnapshot.forEach((docSnapshot) => {
			globalNodeRefs.push(docSnapshot.ref);
		});

		const payload = {
			Group: normalizedNewGroup,
			"credentials.Group": normalizedNewGroup,
		};

		const updatedUserNodes = await updateRefsInChunks(userNodeRefs, payload);
		const updatedGlobalNodes = await updateRefsInChunks(globalNodeRefs, payload);

		invalidateNodeCache(userID);

		return {
			oldGroup: normalizedOldGroup,
			newGroup: normalizedNewGroup,
			matchedUserNodes: userNodeRefs.length,
			updatedUserNodes,
			matchedGlobalNodes: globalNodeRefs.length,
			updatedGlobalNodes,
		};
	} catch (error) {
		console.error("[sensorService][renameNodeGroupForUser] - Error renaming node group:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets a BioNode from the global BioNodes collection by ID
 * @param {string} nodeId - The BioNode ID
 * @returns {Promise<Object|null>} BioNode data or null if not found
 */
export async function getBioNodeById(nodeId) {
	try {
		const docRef = db.collection("BioNodes").doc(nodeId);
		const doc = await docRef.get();

		if (!doc.exists) {
			return null;
		}

		const data = doc.data();
		return {
			...data,
			Id: data.Id || nodeId,
			ID: data.Id || data.ID || nodeId,
		};
	} catch (error) {
		console.error("[sensorService][getBioNodeById] - Error fetching BioNode:", error);
		handleFirestoreError(error);
		return null;
	}
}

/**
 * Calculates the connectivity status of a node based on its last update timestamp
 * @param {Object} node - Node object with timestamp field (t)
 * @param {number} currentTime - Current timestamp in milliseconds (optional)
 * @returns {Object} Status object
 */
export function getNodeConnectivityStatus(node, currentTime = null) {
	const now = currentTime || Date.now();
	const lastUpdate = node?.t || node?.timestamp || 0;
	const timeDiff = now - lastUpdate;

	const FIVE_MINUTES = 5 * 60 * 1000;
	const FIFTEEN_MINUTES = 15 * 60 * 1000;

	if (timeDiff < FIVE_MINUTES) {
		return {
			status: "healthy",
			timeDiff,
			message: "Node is actively reporting",
		};
	} else if (timeDiff < FIFTEEN_MINUTES) {
		return {
			status: "warning",
			timeDiff,
			message: "Node has not reported recently",
		};
	} else {
		return {
			status: "critical",
			timeDiff,
			message: "Node appears offline",
		};
	}
}

/**
 * Gets sensor data for a specific animal device
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @param {string} resolution - Data resolution (Day, Hour, etc.)
 * @param {number} startTime - Optional start timestamp to filter data
 * @returns {Promise<Array>} Array of resampled sensor data points
 */
export async function getSensordata(
	userID,
	mac,
	resolution = "Day",
	startTime = null
) {
	try {
		const interval = 60 * 60 * 1000; // 1 hour in milliseconds

		// Lazy-load severity filter to avoid circular deps
		const { filterSeverity } = await import("../../../utils/severityFilter");

		const docRef = db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.collection("Sensordata")
			.doc(resolution);

		const doc = await docRef.get();

		if (!doc.exists) {
			console.warn(
				`[sensorService][getSensordata] - No sensordata found for userID: ${userID}, mac: ${mac}, resolution: ${resolution}`
			);
			return [];
		}

		const fetchedData = doc.data().Sensordata || [];

		if (!Array.isArray(fetchedData) || fetchedData.length === 0) {
			console.warn(
				`[sensorService][getSensordata] - Sensordata is empty or invalid for userID: ${userID}, mac: ${mac}, resolution: ${resolution}`
			);
			return [];
		}

		const sortedData = fetchedData.sort((a, b) => a.t - b.t);

		let filteredData = sortedData;
		if (startTime) {
			filteredData = sortedData.filter((item) => item.t >= startTime);

			if (filteredData.length === 0) {
				console.warn(
					`[sensorService][getSensordata] - No data found after filtering by timestamp (startTime: ${startTime})`
				);
				return [];
			}
		}

		if (filteredData.length === 0) {
			return [];
		}

		const minTime = Math.floor(filteredData[0].t / interval) * interval;
		const maxTime =
			Math.ceil(filteredData[filteredData.length - 1].t / interval) * interval;

		const resampledData = [];
		let currentIntervalStart = minTime;
		let currentIntervalEnd = minTime + interval;
		let dataIndex = 0;

		const dataLength = filteredData.length;
		let previousValue = null;

		while (currentIntervalStart < maxTime) {
			const intervalData = [];

			while (
				dataIndex < dataLength &&
				filteredData[dataIndex].t < currentIntervalEnd
			) {
				intervalData.push(filteredData[dataIndex]);
				dataIndex++;
			}

			if (intervalData.length > 0) {
				const sum = intervalData.reduce(
					(acc, curr) => {
						acc.a += curr.a || 0;
						acc.aChange += curr.aChange || 0;
						acc.aNeighbours += curr.aNeighbours || 0;
						acc.c += curr.c || 0;
						acc.cChange += curr.cChange || 0;
						acc.cNeighbours += curr.cNeighbours || 0;
						acc.s += curr.s || 0;
						acc.count += 1;
						return acc;
					},
					{
						a: 0,
						aChange: 0,
						aNeighbours: 0,
						c: 0,
						cChange: 0,
						cNeighbours: 0,
						s: 0,
						count: 0,
					}
				);

				const count = sum.count;

				if (count > 0) {
					// Determine worst severity in interval (for display).
					// "critical" (detached ear tag) outranks everything — it used to be
					// missing here, so a critical point could never win its hour.
					const severityPriority = { critical: 4, high: 3, medium: 2, low: 1, none: 0 };
					let worstEntry = intervalData[0];
					for (const item of intervalData) {
						if ((severityPriority[item.severity] || 0) > (severityPriority[worstEntry.severity] || 0)) {
							worstEntry = item;
						}
					}

					const meanValue = {
						t: currentIntervalStart,
						a: sum.a / count,
						aChange: sum.aChange / count,
						aNeighbours: sum.aNeighbours / count,
						c: sum.c / count,
						cChange: sum.cChange / count,
						cNeighbours: sum.cNeighbours / count,
						s: sum.s / count,
					};

					// Apply the v2 severity filter to the resampled mean value. Points
					// scored by a calibrated Watchdog model (modelVersion v3+) pass
					// through untouched — filterSeverity checks the version itself.
					const filtered = filterSeverity({
						...meanValue,
						severity: worstEntry.severity || "none",
						reasons: worstEntry.reasons || [],
						modelVersion: worstEntry.modelVersion,
					});
					meanValue.severity = filtered.severity;
					meanValue.reasons = filtered.reasons;
					if (worstEntry.modelVersion !== undefined) {
						meanValue.modelVersion = worstEntry.modelVersion;
					}

					resampledData.push(meanValue);
					previousValue = meanValue;
				}
			} else if (previousValue) {
				let nextDataIndex = dataIndex;
				while (
					nextDataIndex < dataLength &&
					filteredData[nextDataIndex].t < currentIntervalEnd
				) {
					nextDataIndex++;
				}

				const nextValue = filteredData[nextDataIndex];

				if (nextValue) {
					const timeDiff = nextValue.t - previousValue.t;
					const factor = (currentIntervalStart - previousValue.t) / timeDiff;
					const clampedFactor = Math.min(Math.max(factor, 0), 1);

					const interpolatedValue = {
						t: currentIntervalStart,
						a:
							previousValue.a + clampedFactor * (nextValue.a - previousValue.a),
						aChange:
							previousValue.aChange +
							clampedFactor * (nextValue.aChange - previousValue.aChange),
						aNeighbours:
							previousValue.aNeighbours +
							clampedFactor *
								(nextValue.aNeighbours - previousValue.aNeighbours),
						c:
							previousValue.c + clampedFactor * (nextValue.c - previousValue.c),
						cChange:
							previousValue.cChange +
							clampedFactor * (nextValue.cChange - previousValue.cChange),
						cNeighbours:
							previousValue.cNeighbours +
							clampedFactor *
								(nextValue.cNeighbours - previousValue.cNeighbours),
						s:
							previousValue.s + clampedFactor * (nextValue.s - previousValue.s),
					};

					resampledData.push(interpolatedValue);
				}
			}

			currentIntervalStart += interval;
			currentIntervalEnd += interval;
		}

		return resampledData;
	} catch (error) {
		console.error("[sensorService][getSensordata] - Error fetching sensordata:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets node positions
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array>} Array of node position objects
 */
export async function getNodePositions(userId) {
	try {
		let positions = [];

		await db
			.collection("Users")
			.doc(userId)
			.collection("RoomPositions")
			.get()
			.then((snapshot) => {
				snapshot.forEach((doc) => {
					let position = doc.data();
					position.roomId = doc.id;
					positions.push(position);
				});
			})
			.catch((e) => {
				throw e;
			});

		return positions;
	} catch (error) {
		console.error("[sensorService][getNodePositions] - Error getting node positions:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Sets node position
 * @param {string} userId - The unique identifier for the user
 * @param {string} node - Node ID
 * @param {Object} position - Position data
 * @returns {Promise<void>}
 */
export async function setNodePosition(userId, node, position) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("RoomPositions")
			.doc(node)
			.set(position)
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("[sensorService][setNodePosition] - Error setting node position:", error);
		handleFirestoreError(error);
		throw error;
	}
}

/**
 * Gets environment data for a group of nodes
 * @param {string} userID - The unique identifier for the user
 * @param {Array} nodes - Array of node objects
 * @returns {Promise<Array>} Array of environment data points
 */
export async function getEnviromentDataOfGroup(userID, nodes) {
	let data = [];

	if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
		return [];
	}

	try {

		const promises = nodes.map(async (node) => {
			if (!node) {
				return Promise.resolve();
			}

			const nodeId = node.Id;

			if (!nodeId) {
				return Promise.resolve();
			}

			return db
				.collection("Users")
				.doc(userID)
				.collection("Nodes")
				.doc(nodeId)
				.collection("Enviroment")
				.doc("Sensordata")
				.get()
				.then((snapshot) => {
					const sensordata = snapshot.data();

					if (sensordata && sensordata.Data && Array.isArray(sensordata.Data)) {
						sensordata.Data.forEach((dataPoint) => {
							if (dataPoint && dataPoint.t) {
								data.push({
									t: dataPoint.t,
									Humidity: dataPoint.Humidity || 0,
									Temperature: dataPoint.Temperature || 0,
								});
							}
						});
					}
				})
				.catch((error) => {
					console.error("[sensorService][getEnviromentDataOfGroup] - Error fetching data for node", nodeId, ":", error);
				});
		});

		await Promise.all(promises);
	} catch (error) {
		console.error("[sensorService][getEnviromentDataOfGroup] - Error:", error);
		handleFirestoreError(error);
		return [];
	}

	if (data.length === 0) {
		return [];
	}

	data.sort((a, b) => a.t - b.t);

	const runningMeanData = [];
	for (let i = 0; i < data.length; i++) {
		if (i >= 4) {
			let sumHumidity = 0;
			let sumTemperature = 0;
			for (let j = i - 4; j <= i; j++) {
				sumHumidity += isNaN(+data[j].Humidity) ? 0 : +data[j].Humidity;
				sumTemperature += isNaN(+data[j].Temperature)
					? 0
					: +data[j].Temperature;
			}
			runningMeanData.push({
				t: data[i].t,
				Humidity: sumHumidity / 5,
				Temperature: sumTemperature / 5,
			});
		}
	}

	return runningMeanData;
}

/**
 * Gets all ants for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array>} Array of ant objects with activity status
 */
export async function getAnts(userId) {
	try {
		let ants = [];

		await db
			.collection("Users")
			.doc(userId)
			.collection("Ants")
			.get()
			.then((snapshot) => {
				snapshot.forEach((doc) => {
					let document = doc.data();
					document.isActive =
						new Date().getTime() - document.t < 1000 * 60 * 60 * 24;
					ants.push(document);
				});
			})
			.catch((e) => {
				throw e;
			});

		return ants;
	} catch (error) {
		console.error("[sensorService][getAnts] - Error getting ants:", error);
		handleFirestoreError(error);
		throw error;
	}
}
